import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '@/api/axiosConfig.js';
import { clearStoredSession, getStoredToken, syncStoredSession, syncStoredToken } from '@/utils/authStorage.js';
import { getTenantFromBrowser } from '@/utils/tenant.js';
import { isChatModuleUnavailableError, sendChatHeartbeat } from '@/services/chat.js';
import { can, canAny, hasLevelAtLeast, hasRole } from '@/security/access.js';

const SESSION_LOCAL_KEYS_TO_CLEAR = [
  'emision-guia-v3-draft',
];
const USER_DAILY_USAGE_PREFIX = 'ssv3_user_daily_usage';
const USER_DAILY_USAGE_MAX_GAP_MS = 5 * 60 * 1000;
const USER_DAILY_USAGE_WINDOW_DAYS = 30;

const AuthContext = createContext({
  isAuth: false,
  user: null,
  tenant: null,
  sucursal: null,
  sucursalPrincipal: null,
  sucursalActiva: null,
  sucursalesHabilitadas: [],
  puedeCambiarSucursal: false,
  token: null,
  login: () => {},
  logout: () => {},
  setSucursalActiva: async () => {},
  can: () => false,
  canAny: () => false,
  hasRole: () => false,
  hasLevelAtLeast: () => false,
});

function buildSessionState({ user, tenant, token }) {
  const sucursalPrincipal = user?.sucursalPrincipal || user?.sucursal_principal || user?.sucursal || null;
  const sucursalActiva = user?.sucursalActiva || user?.sucursal_activa || user?.sucursal || sucursalPrincipal || null;
  return {
    user,
    tenant,
    token,
    sucursal: sucursalActiva,
    sucursalPrincipal,
    sucursalActiva,
    sucursalesHabilitadas: user?.sucursalesHabilitadas || [],
    puedeCambiarSucursal: Boolean(user?.puedeCambiarSucursal),
  };
}

function getUserUsageStorageKey(user, tenant) {
  if (!user?.id) return null;
  const tenantKey = tenant?.code || tenant?.id || user?.empresa_id || user?.empresaId || 'tenant';
  return `${USER_DAILY_USAGE_PREFIX}:${tenantKey}:${user.id}`;
}

function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function readDailyUsage(key) {
  if (!key || typeof window === 'undefined') return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function calculateAverageDailyHours(usage) {
  const values = Object.entries(usage || {})
    .sort(([a], [b]) => b.localeCompare(a))
    .slice(0, USER_DAILY_USAGE_WINDOW_DAYS)
    .map(([, milliseconds]) => Number(milliseconds || 0))
    .filter((milliseconds) => milliseconds > 0);

  if (!values.length) return null;
  const averageMs = values.reduce((sum, milliseconds) => sum + milliseconds, 0) / values.length;
  return Math.round((averageMs / 3600000) * 10) / 10;
}

function pruneDailyUsage(usage) {
  return Object.fromEntries(
    Object.entries(usage || {})
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, USER_DAILY_USAGE_WINDOW_DAYS),
  );
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => buildSessionState({ user: null, tenant: null, token: null }));
  const [hydrated, setHydrated] = useState(false);

  const applySession = useCallback(({ user, tenant, token }) => {
    const nextSession = buildSessionState({ user, tenant, token });
    setSession(nextSession);
    syncStoredSession({ token, user, tenant });
    return nextSession;
  }, []);

  useEffect(() => {
    let mounted = true;

    const restoreSession = async () => {
      try {
        const storedToken = getStoredToken();
        const storedUser = localStorage.getItem('user');
        const storedTenant = localStorage.getItem('tenant');

        if (!storedToken || !storedUser) {
          if (mounted) setHydrated(true);
          return;
        }

        syncStoredToken(storedToken);
        const parsedUser = JSON.parse(storedUser);
        const parsedTenant = storedTenant ? JSON.parse(storedTenant) : null;
        if (mounted) {
          setSession(buildSessionState({ user: parsedUser, tenant: parsedTenant, token: storedToken }));
        }

        try {
          const response = await api.get('/auth/verify', { skipAuthRedirect: true });
          if (mounted && response?.data?.valid) {
            applySession({
              token: response.data.token || storedToken,
              user: response.data.user || parsedUser,
              tenant: response.data.tenant || parsedTenant,
            });
          }
        } catch (error) {
          console.warn('No se pudo rehidratar la sesión desde backend', error);
        }
      } catch (error) {
        console.warn('No se pudo recuperar la sesión', error);
        clearStoredSession();
      } finally {
        if (mounted) setHydrated(true);
      }
    };

    restoreSession();
    return () => {
      mounted = false;
    };
  }, [applySession]);

  useEffect(() => {
    if (!session.token || !session.user?.id) {
      return undefined;
    }

    let active = true;
    const syncPresence = async (status = 'online') => {
      if (!active) return;

      try {
        const storedTenant = localStorage.getItem('tenant');
        if (!storedTenant) {
          getTenantFromBrowser();
        }
        await sendChatHeartbeat(status);
      } catch (error) {
        if (isChatModuleUnavailableError(error)) return;
        console.warn('No se pudo sincronizar presencia de chat', error);
      }
    };

    const handleVisibility = () => {
      syncPresence(document.hidden ? 'away' : 'online');
    };

    syncPresence('online');
    const intervalId = window.setInterval(() => {
      syncPresence(document.hidden ? 'away' : 'online');
    }, 30000);

    window.addEventListener('focus', handleVisibility);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleVisibility);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [session.token, session.user?.id]);

  useEffect(() => {
    if (!session.token || !session.user?.id || typeof window === 'undefined') {
      return undefined;
    }

    const storageKey = getUserUsageStorageKey(session.user, session.tenant);
    if (!storageKey) return undefined;

    let lastSeenAt = Date.now();
    let active = true;

    const syncAverage = (averageHours) => {
      setSession((current) => {
        if (!current.user?.id || String(current.user.id) !== String(session.user.id)) {
          return current;
        }
        if (current.user.promedioHorasDiarias === averageHours) {
          return current;
        }
        return {
          ...current,
          user: {
            ...current.user,
            promedioHorasDiarias: averageHours,
          },
        };
      });
    };

    const registerUsage = () => {
      if (!active || document.hidden) {
        lastSeenAt = Date.now();
        return;
      }

      const now = Date.now();
      const elapsed = Math.min(now - lastSeenAt, USER_DAILY_USAGE_MAX_GAP_MS);
      lastSeenAt = now;
      if (elapsed <= 0) return;

      const today = getLocalDateKey(new Date(now));
      const usage = readDailyUsage(storageKey);
      usage[today] = Number(usage[today] || 0) + elapsed;
      const prunedUsage = pruneDailyUsage(usage);
      window.localStorage.setItem(storageKey, JSON.stringify(prunedUsage));
      syncAverage(calculateAverageDailyHours(prunedUsage));
    };

    const handleVisibility = () => {
      lastSeenAt = Date.now();
      if (!document.hidden) {
        registerUsage();
      }
    };

    syncAverage(calculateAverageDailyHours(readDailyUsage(storageKey)));
    const intervalId = window.setInterval(registerUsage, 60000);
    window.addEventListener('focus', handleVisibility);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      registerUsage();
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleVisibility);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [session.token, session.user?.id, session.tenant]);

  const login = useCallback((userData, extras = {}) => {
    const storedToken = extras.token || getStoredToken();
    const storedTenant = extras.tenant || (() => {
      try {
        const raw = localStorage.getItem('tenant');
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    })();
    applySession({
      user: userData,
      token: storedToken,
      tenant: storedTenant,
    });
  }, [applySession]);

  const logout = useCallback(() => {
    setSession(buildSessionState({ user: null, tenant: null, token: null }));
    clearStoredSession();
    if (typeof window !== 'undefined') {
      SESSION_LOCAL_KEYS_TO_CLEAR.forEach((key) => window.localStorage.removeItem(key));
    }
  }, []);

  const setSucursalActiva = useCallback(async (sucursalId, options = {}) => {
    const response = await api.post('/auth/cambiar-sucursal-activa', {
      sucursal_id: Number(sucursalId),
      motivo: options.motivo || null,
    });
    const nextSession = applySession({
      token: response.data.token,
      user: response.data.user,
      tenant: session.tenant,
    });
    if (typeof window !== 'undefined') {
      SESSION_LOCAL_KEYS_TO_CLEAR.forEach((key) => window.localStorage.removeItem(key));
      window.dispatchEvent(new CustomEvent('sinamon:sucursal-activa-cambiada', {
        detail: {
          sucursalId: Number(sucursalId),
          userId: nextSession.user?.id || null,
        },
      }));
    }
    return nextSession;
  }, [applySession, session.tenant]);

  const value = useMemo(() => {
    const isAuth = Boolean(session.user && session.token);
    return {
      ...session,
      isAuth,
      isAuthenticated: isAuth,
      hydrated,
      login,
      logout,
      setSucursalActiva,
      can: (permissionCode) => can(session.user, permissionCode),
      canAny: (permissionCodes) => canAny(session.user, permissionCodes),
      hasRole: (roleCode) => hasRole(session.user, roleCode),
      hasLevelAtLeast: (level) => hasLevelAtLeast(session.user, level),
    };
  }, [session, hydrated, login, logout, setSucursalActiva]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe utilizarse dentro de un AuthProvider');
  }
  return context;
}
